import { useTranslation } from "react-i18next";

import { Stack } from "@mui/material";

import { ClientContent } from "../../../client-content/ClientContent";
import { useClientFileContext } from "../../../context/ClientFileContext";
import { ChecklistQuestion } from "../checklist-question/ChecklistQuestion";
import { ChecklistSectionCard } from "../section-card/ChecklistSectionCard";
import { getQuestionStates } from "./SpecialDebts.utils";
import { useSpecialDebts } from "./hooks/useSpecialDebts";

const BASE = "clients.client.debtSettlement.bankruptcyChecklist.specialDebts";

export function SpecialDebts() {
  const { t } = useTranslation();
  const { clientId } = useClientFileContext();
  const { formik, setAnswer, setRemark } = useSpecialDebts(clientId);

  const values = formik.values;
  const states = getQuestionStates(values);

  return (
    <ClientContent formik={formik}>
      <Stack gap={2}>
        <ChecklistSectionCard title={t(`${BASE}.sections.selbststaendigkeit`)}>
          <ChecklistQuestion
            question={t(`${BASE}.questions.fruehereSelbststaendigkeit`)}
            value={values.wasPreviouslySelfEmployed}
            onChange={(value) => setAnswer("wasPreviouslySelfEmployed", value)}
            conditionalInfo={values.wasPreviouslySelfEmployed === true ? t(`${BASE}.info.vergInfo`) : undefined}
          />
          <ChecklistQuestion
            question={t(`${BASE}.questions.aktuelleSelbststaendigkeit`)}
            value={values.isCurrentlySelfEmployed}
            onChange={(value) => setAnswer("isCurrentlySelfEmployed", value)}
          />
          {states.hasBusinessLicense.visible && (
            <ChecklistQuestion
              question={t(`${BASE}.questions.aktuellerGewerbeschein`)}
              value={values.hasBusinessLicense}
              onChange={(value) => setAnswer("hasBusinessLicense", value)}
            />
          )}
          {states.exercisesTradeAsBusiness.visible && (
            <ChecklistQuestion
              question={t(`${BASE}.questions.gewerbe`)}
              value={values.exercisesTradeAsBusiness}
              onChange={(value) => setAnswer("exercisesTradeAsBusiness", value)}
              conditionalInfo={values.exercisesTradeAsBusiness === false ? t(`${BASE}.info.aktNichtGew`) : undefined}
            />
          )}
          {states.isBusinessLicenseDormant.visible && (
            <ChecklistQuestion
              question={t(`${BASE}.questions.ruhend`)}
              value={values.isBusinessLicenseDormant}
              onChange={(value) => setAnswer("isBusinessLicenseDormant", value)}
              conditionalInfo={values.isBusinessLicenseDormant !== null ? t(`${BASE}.info.gewRuhend`) : undefined}
            />
          )}
        </ChecklistSectionCard>

        <ChecklistSectionCard title={t(`${BASE}.sections.unterhaltsrueckstaende`)}>
          <ChecklistQuestion
            question={t(`${BASE}.questions.unterhaltsrueckstaende`)}
            value={values.hasMaintenanceArrears}
            onChange={(value) => setAnswer("hasMaintenanceArrears", value)}
            conditionalInfo={values.hasMaintenanceArrears === true ? t(`${BASE}.info.unthInfo`) : undefined}
          />
        </ChecklistSectionCard>

        <ChecklistSectionCard title={t(`${BASE}.sections.deliktschulden`)}>
          <ChecklistQuestion
            question={t(`${BASE}.questions.deliktschulden`)}
            value={values.hasCriminalDebts}
            onChange={(value) => setAnswer("hasCriminalDebts", value)}
            info={t(`${BASE}.info.infoDelikt`)}
            conditionalInfo={values.hasCriminalDebts === true ? t(`${BASE}.info.deliktInfo`) : undefined}
          />
        </ChecklistSectionCard>

        <ChecklistSectionCard title={t(`${BASE}.sections.zuletztGemachteSchulden`)}>
          <ChecklistQuestion
            question={t(`${BASE}.questions.kreditschulden`)}
            value={values.hasCreditDebts}
            onChange={(value) => setAnswer("hasCreditDebts", value)}
          />
          {states.creditWithinLastFiveYears.visible && (
            <ChecklistQuestion
              question={t(`${BASE}.questions.kreditzeitpunkt`)}
              value={values.creditWithinLastFiveYears}
              onChange={(value) => setAnswer("creditWithinLastFiveYears", value)}
              answerLabels={{ yes: t(`${BASE}.answers.kreditWithin`), no: t(`${BASE}.answers.kreditBefore`) }}
              warning={{ active: states.creditWithinLastFiveYears.isWarning, text: t(`${BASE}.warnings.kreditWarn`) }}
              remark={
                values.creditWithinLastFiveYears === true
                  ? {
                      label: t(`${BASE}.remarkLabel`),
                      value: values.creditRemark,
                      onChange: (value) => setRemark("creditRemark", value),
                    }
                  : undefined
              }
            />
          )}

          <ChecklistQuestion
            question={t(`${BASE}.questions.versandhausschulden`)}
            value={values.hasMailOrderDebts}
            onChange={(value) => setAnswer("hasMailOrderDebts", value)}
          />
          {states.mailOrderWithinLastThreeYears.visible && (
            <ChecklistQuestion
              question={t(`${BASE}.questions.versandzeitpunkt`)}
              value={values.mailOrderWithinLastThreeYears}
              onChange={(value) => setAnswer("mailOrderWithinLastThreeYears", value)}
              answerLabels={{ yes: t(`${BASE}.answers.versandWithin`), no: t(`${BASE}.answers.versandBefore`) }}
              warning={{
                active: states.mailOrderWithinLastThreeYears.isWarning,
                text: t(`${BASE}.warnings.versandWarn`),
              }}
              remark={
                values.mailOrderWithinLastThreeYears === true
                  ? {
                      label: t(`${BASE}.remarkLabel`),
                      value: values.mailOrderRemark,
                      onChange: (value) => setRemark("mailOrderRemark", value),
                    }
                  : undefined
              }
            />
          )}
          {states.hasNeverPaidDebts.visible && (
            <ChecklistQuestion
              question={t(`${BASE}.questions.nieGezahlt`)}
              value={values.hasNeverPaidDebts}
              onChange={(value) => setAnswer("hasNeverPaidDebts", value)}
              warning={{ active: states.hasNeverPaidDebts.isWarning, text: t(`${BASE}.warnings.nieGezWarn`) }}
            />
          )}
        </ChecklistSectionCard>
      </Stack>
    </ClientContent>
  );
}
